import html2canvas from 'html2canvas';
import { useCanvasStore } from '@/stores/canvasStore';

const EXPORT_TARGET = '[data-canvas-export-target]';
const PADDING = 80;
const FALLBACK_BACKGROUND = '#ffffff';
const EXPORT_SCALE = 2;

/**
 * Which elements to leave out of the export. html2canvas clones the WHOLE
 * document and runs this on every node, ancestors of the target included, so
 * only chrome inside the canvas may be skipped. Skipping an ancestor (the app
 * shell is `fixed inset-0`) drops the canvas with it and html2canvas fails
 * with "Unable to find element in cloned iframe".
 */
export function shouldIgnoreForExport(element: Element, target: Element): boolean {
  if (element === target || !target.contains(element)) return false;
  if (element.hasAttribute('data-export-ignore')) return true;
  if (element.hasAttribute('data-canvas-overlay')) return true;
  if (element.classList.contains('fixed')) return true;
  return false;
}

/**
 * html2canvas measures each font's baseline in the LIVE document with a 1px
 * <img> placed after a line of text. Tailwind's preflight makes every img
 * `display: block`, which drops that probe onto the next line, so all text was
 * painted about a line too low (and cut off in single-line `truncate` labels
 * such as folder names). Keeps only that probe inline while a capture runs.
 */
const FONT_PROBE_FIX =
  'body > div[style*="visibility: hidden"] > img[width="1"][height="1"] { display: inline !important; }';

/**
 * html2canvas paints the brand shadows (negative spread) as a thick band
 * inside every card instead of a soft drop shadow, so the export leaves them
 * out. Applied to the clone html2canvas paints from; the live page is untouched.
 */
const CLONE_FIX = `${EXPORT_TARGET} * { box-shadow: none !important; }`;

function addStyle(doc: Document, css: string): HTMLStyleElement {
  const style = doc.createElement('style');
  style.textContent = css;
  doc.head.appendChild(style);
  return style;
}

function canvasToJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not create the image. Try again.'))),
      'image/jpeg',
      0.8,
    );
  });
}

function download(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    link.remove();
    URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Fits every item on the current board into view, captures the canvas as a
 * JPG, downloads it and puts the view back. Throws an Error with a message
 * that can be shown to the user as is.
 */
export async function exportCanvasToJPG(): Promise<void> {
  const { items, currentBoardId, boards, zoom, panX, panY, setZoom, setPan } = useCanvasStore.getState();

  const boardItems = items.filter(i => i.board_id === currentBoardId);
  if (boardItems.length === 0) throw new Error('Nothing on this board to export yet.');

  const target = document.querySelector<HTMLElement>(EXPORT_TARGET);
  if (!target) throw new Error('Could not find the canvas. Reload and try again.');

  // Bounding box of every item on this board
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const item of boardItems) {
    minX = Math.min(minX, item.position_x);
    minY = Math.min(minY, item.position_y);
    maxX = Math.max(maxX, item.position_x + item.width);
    maxY = Math.max(maxY, item.position_y + item.height);
  }

  // Zoom that fits all items in the canvas with padding: never above 100%, never below 10%
  const { width: viewWidth, height: viewHeight } = target.getBoundingClientRect();
  const fitX = (viewWidth - PADDING * 2) / Math.max(1, maxX - minX);
  const fitY = (viewHeight - PADDING * 2) / Math.max(1, maxY - minY);
  let exportZoom = Math.max(0.1, Math.min(fitX, fitY, 1));
  // Dense boards (15+ items) zoom out a little more for an overview feel
  if (boardItems.length > 15) exportZoom *= 0.85;

  setZoom(exportZoom);
  setPan(viewWidth / 2 / exportZoom - (minX + maxX) / 2, viewHeight / 2 / exportZoom - (minY + maxY) / 2);

  try {
    // Let the canvas re-render (and load newly visible items) at the export view
    await new Promise(resolve => setTimeout(resolve, 800));

    const background = getComputedStyle(target).backgroundColor;
    const probeFix = addStyle(document, FONT_PROBE_FIX);
    let captured: HTMLCanvasElement;
    try {
      captured = await html2canvas(target, {
        // JPG has no alpha, so fill with the canvas colour (light or dark)
        backgroundColor: background && background !== 'rgba(0, 0, 0, 0)' ? background : FALLBACK_BACKGROUND,
        // 2x so text stays sharp on zoomed-out boards (about 4x the pixels of 1x)
        scale: EXPORT_SCALE,
        useCORS: true,
        allowTaint: false,
        logging: false,
        foreignObjectRendering: false,
        removeContainer: true,
        ignoreElements: (element) => shouldIgnoreForExport(element, target),
        onclone: (clonedDoc) => {
          addStyle(clonedDoc, CLONE_FIX);
        },
      });
    } catch (err) {
      // html2canvas rejects with plain strings, not Errors
      console.error('Export error:', err);
      throw new Error('Could not capture the canvas. Try again.');
    } finally {
      probeFix.remove();
    }

    const blob = await canvasToJpeg(captured);
    const boardName = boards.find(b => b.id === currentBoardId)?.name || 'canvas';
    const safeName = boardName.replace(/[^a-zA-Z0-9_-]/g, '_');
    const date = new Date().toISOString().slice(0, 10);
    download(blob, `${safeName}_${date}.jpg`);
  } finally {
    setZoom(zoom);
    setPan(panX, panY);
  }
}
